import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { X, Plus, UserCircle, Lock } from "lucide-react";

// Lets a logged-in user create up to 3 alias display names (same naming rules as usernames).
// Once created, an alias can only be changed/removed by the super admin.
export const AliasManager = ({ token, onClose, onChanged }) => {
  const [data, setData] = useState({ username: "", aliases: [], max_aliases: 3, can_add: true });
  const [newAlias, setNewAlias] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchAliases = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/aliases`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load aliases");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAliases(); }, [fetchAliases]);

  const validate = (name) => {
    if (name.length < 8 || name.length > 16) return "Alias must be 8-16 characters";
    if (!/^[a-zA-Z0-9_]+$/.test(name)) return "Only letters, numbers and underscores allowed";
    if (name.startsWith("_") || name.endsWith("_")) return "Cannot start or end with an underscore";
    if (name.includes("__")) return "No two underscores in a row";
    return null;
  };

  const handleCreate = async () => {
    const name = newAlias.trim();
    const err = validate(name);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/aliases`, { name }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Alias "${name}" created!`);
      setNewAlias("");
      await fetchAliases();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create alias");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[9990] flex items-center justify-center p-3" data-testid="alias-manager-modal">
      <div className="retro-window w-full max-w-md">
        <div className="retro-title-bar flex items-center justify-between">
          <span className="flex items-center gap-2"><UserCircle className="w-4 h-4" /> My Aliases</span>
          <button onClick={onClose} data-testid="close-alias-manager" className="hover:bg-white/20 px-1"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 bg-[#ECE9D8] space-y-3">
          <p className="text-xs text-gray-700">
            Create up to <b>{data.max_aliases}</b> alias names. In a chat room you can appear as one of these
            instead of your username. <b>Once created, only a super admin can change or remove an alias.</b>
          </p>

          <div className="bg-white border border-gray-300 rounded p-2">
            <div className="text-[11px] text-gray-500 mb-1">Your username</div>
            <div className="font-bold text-sm text-[#7B0099]">{data.username}</div>
          </div>

          {loading ? (
            <p className="text-xs text-gray-500">Loading…</p>
          ) : (
            <div className="space-y-1" data-testid="alias-list">
              {data.aliases.length === 0 && <p className="text-xs text-gray-500 italic">No aliases yet.</p>}
              {data.aliases.map((a) => (
                <div key={a.name} className="flex items-center justify-between bg-white border border-gray-300 rounded px-2 py-1" data-testid={`alias-item-${a.name}`}>
                  <span className="text-sm font-medium">{a.name}</span>
                  <span className="text-[10px] text-gray-400 flex items-center gap-1"><Lock className="w-3 h-3" /> locked</span>
                </div>
              ))}
            </div>
          )}

          {data.can_add ? (
            <div className="flex gap-2 pt-1">
              <input
                value={newAlias}
                onChange={(e) => setNewAlias(e.target.value)}
                placeholder="New alias (8-16 chars)"
                maxLength={16}
                className="retro-input flex-1 text-sm"
                data-testid="new-alias-input"
              />
              <button onClick={handleCreate} disabled={saving} className="retro-button flex items-center gap-1 text-sm px-3" data-testid="create-alias-btn">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
          ) : (
            <p className="text-xs text-orange-700">You've reached the maximum of {data.max_aliases} aliases.</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Small picker shown when entering a room: choose which name to appear as + online-light toggle.
export const RoomIdentityPicker = ({ username, aliases, room, onConfirm, onCancel }) => {
  const [selected, setSelected] = useState("");     // "" = real username
  const [showOnline, setShowOnline] = useState(true);
  return (
    <div className="fixed inset-0 bg-black/50 z-[9990] flex items-center justify-center p-3" data-testid="room-identity-picker">
      <div className="retro-window w-full max-w-sm">
        <div className="retro-title-bar flex items-center justify-between">
          <span>Enter {room?.name || "room"} as…</span>
        </div>
        <div className="p-4 bg-[#ECE9D8] space-y-3">
          <div className="space-y-1">
            <label className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${selected === "" ? "border-purple-500 bg-purple-50" : "border-gray-300 bg-white"}`}>
              <input type="radio" checked={selected === ""} onChange={() => setSelected("")} data-testid="identity-real" />
              <span className="text-sm font-bold">{username} <span className="text-[10px] text-gray-500">(your username)</span></span>
            </label>
            {aliases.map((a) => (
              <label key={a.name} className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${selected === a.name ? "border-purple-500 bg-purple-50" : "border-gray-300 bg-white"}`}>
                <input type="radio" checked={selected === a.name} onChange={() => setSelected(a.name)} data-testid={`identity-alias-${a.name}`} />
                <span className="text-sm">{a.name} <span className="text-[10px] text-gray-500">(alias)</span></span>
              </label>
            ))}
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={showOnline} onChange={(e) => setShowOnline(e.target.checked)} data-testid="identity-online-toggle" />
            Show my online light (green dot)
          </label>

          <div className="flex gap-2 pt-1">
            <button onClick={onCancel} className="retro-button flex-1 text-sm" data-testid="identity-cancel">Cancel</button>
            <button
              onClick={() => onConfirm({ alias: selected || null, show_online: showOnline })}
              className="retro-button flex-1 text-sm bg-gradient-to-r from-purple-200 to-yellow-200 font-bold"
              data-testid="identity-enter"
            >Enter</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AliasManager;
